import { ArrowRightIcon } from '@/components/ui/icons'
import { formatDate } from '@/dates'
import type {
	SubmissionActivityActionType,
	SubmissionActivityEntry,
	SubmissionActivityStage,
	SubmissionActivityStatus
} from '@/features/luxe-submissions/types'
import { type TKey, useTranslation } from '@/translations/i18n'

interface ListingActivityEntryProps {
	entry: SubmissionActivityEntry
}

export const StatusToLabelTKey = {
	back_in_review: 'luxe_submissions:status_back_in_review',
	completed: 'luxe_submissions:status_completed',
	failed: 'luxe_submissions:status_failed',
	failed_standards: 'luxe_submissions:status_failed_standards',
	in_progress: 'luxe_submissions:status_in_progress',
	locked: 'luxe_submissions:status_locked',
	needs_review: 'luxe_submissions:status_needs_review',
	needs_review_mms: 'luxe_submissions:status_needs_review_mms',
	passed: 'luxe_submissions:status_passed',
	pending: 'luxe_submissions:status_pending',
	pre_onboarding: 'luxe_submissions:status_pre_onboarding',
	ready_for_inspection: 'luxe_submissions:status_ready_for_inspection',
	rejected: 'luxe_submissions:status_rejected',
	skipped: 'luxe_submissions:status_skipped',
	submitted: 'luxe_submissions:status_submitted',
	pending_review: 'luxe_submissions:status_pending_review',
	accepted: 'luxe_submissions:status_accepted',
	ml_review: 'luxe_submissions:status_ml_review',
	ready: 'luxe_submissions:status_ready',
	blocked: 'luxe_submissions:status_blocked',
	quote_generated: 'luxe_submissions:status_quote_generated'
} as const satisfies Record<SubmissionActivityStatus, TKey>

export const ActivityStageToLabelTKey = {
	host_submission: 'luxe_submissions:host_submission',
	precheck: 'luxe_submissions:pre_check',
	visual_vetting: 'luxe_submissions:visual_vetting',
	final_review: 'luxe_submissions:final_review',
	quote_generation: 'luxe_submissions:quote_generation'
} as const satisfies Record<SubmissionActivityStage, TKey>

export const ActionTypeToLabelTKey = {
	submission_created: 'luxe_submissions:activity_action_submission_created',
	claimed: 'luxe_submissions:activity_action_claimed',
	assigned: 'luxe_submissions:activity_action_assigned',
	unassigned: 'luxe_submissions:activity_action_unassigned',
	status_updated: 'luxe_submissions:activity_action_status_updated',
	questionnaire_submitted:
		'luxe_submissions:activity_action_questionnaire_submitted',
	decision_made: 'luxe_submissions:activity_action_decision_made',
	escalated: 'luxe_submissions:activity_action_escalated',
	second_review_requested:
		'luxe_submissions:activity_action_second_review_requested'
} as const satisfies Record<SubmissionActivityActionType, TKey>

export function ListingActivityEntry({ entry }: ListingActivityEntryProps) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-col gap-2 border-neutral-200 border-b pb-4 last:border-none'>
			<span className='text-neutral-500 text-sm'>
				{formatDate(entry.createdAt, 'PPp')}
			</span>
			<div className='flex items-center gap-2'>
				<span className='font-medium text-neutral-900 text-sm'>
					{entry.reviewerName}
				</span>
				<span className='font-medium text-neutral-500 text-sm'>
					{t(ActivityStageToLabelTKey[entry.stage])}
				</span>
			</div>
			{entry.fromStatus && entry.toStatus && (
				<div className='flex items-center gap-2'>
					<span className='inline-flex rounded-full border border-neutral-200 bg-neutral-25 px-2.5 py-0.5 font-medium text-neutral-500 text-xs'>
						{t(StatusToLabelTKey[entry.fromStatus])}
					</span>
					<ArrowRightIcon className='text-neutral-400' size={16} />
					<span className='inline-flex rounded-full border border-neutral-200 bg-neutral-25 px-2.5 py-0.5 font-medium text-neutral-500 text-xs'>
						{t(StatusToLabelTKey[entry.toStatus])}
					</span>
				</div>
			)}
			<span className='text-neutral-500 text-xs'>
				{t(ActionTypeToLabelTKey[entry.actionType])}
			</span>
			{entry.note && (
				<div className='flex flex-col gap-1 rounded-md bg-neutral-25 p-2'>
					<span className='font-medium text-neutral-900 text-xs'>
						{t('luxe_submissions:activity_note_label')}
					</span>
					<p className='whitespace-pre-wrap text-neutral-700 text-sm'>
						{entry.note}
					</p>
				</div>
			)}
		</div>
	)
}
